import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {InputTextModule} from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { concatMap, map, Observable, of, switchMap, takeUntil, tap } from 'rxjs';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';

import { WorkerService } from '@layout/driver/services/worker.service';
import { Driver } from '@commons/models/domain/Driver';
import { WorkerModel } from '@shared/models/worker.model';
import { WorkerHttpService } from '@layout/driver/http-services/worker-http.service';
import { DriverLicesnseStatus } from '@src/app/enum/driver-license.enum';
import { driverLicenseNumberValidator } from '@src/app/utils/validators-forms/driver-license-number';
import { DriverLicenseHintComponent } from '../driver-license-hint/driver-license-hint.component';
import { Destroyable } from '@src/app/utils/Destroyable';
import { UserOrganizationsUnitsService } from '@src/app/services/api/user-organizations-units.service';
import { IOrganizationUnit } from '@src/app/interfaces/api/organization-units.interface';
import { UsersWorkersService } from '@src/app/services/api/users-workers.service';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { ResetPasswordResponse } from '@commons/models/remote.response/ResetPasswordResponse';
import { PermissionService } from '@shared/services/permission.service';
import { TreeSelectionObjectsComponent } from '@src/app/standalone-components/tree-selection-objects/tree-selection-objects.component';
import { NotificationService } from '@commons/services/notification/notification.service';

@Component({
  selector: 'app-workers-add-form',
  standalone: true,
  providers: [DialogService],
  imports: [CommonModule, InputTextModule, TreeSelectModule, ButtonModule, DriverLicenseHintComponent, FormsModule, SelectModule, ReactiveFormsModule, TranslateModule, TooltipModule, ToggleSwitchModule, MultiSelectModule],
  templateUrl: './workers-add-form.component.html',
  styleUrls: ['./workers-add-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkersAddFormComponent extends Destroyable implements OnInit, OnDestroy {

  @Output() public onClickBack$: EventEmitter<void> = new EventEmitter();

  public form: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    phone: new FormControl(''),
    driverLicense: new FormControl('', [driverLicenseNumberValidator()]),
    email: new FormControl(''),
    mycarapp: new FormControl(false),
    organization: new FormControl('', [Validators.required]),
  });
  public isSaving: boolean;
  public isAddNewWorker: boolean = true;
  public editedWorkerData?: Driver;
  private organizations: IOrganizationUnit[] = [];
  public treeNodeOrganizations: TreeNode<IOrganizationUnit>[] = [];

  public selectedOrganization = signal<IOrganizationUnit>(null as any);
  private organizationModalRef: DynamicDialogRef<TreeSelectionObjectsComponent>;

  public hasAccessToCreateAccountUser = this.permissionService.hasCardFiles_FUsers();

  private get workerDataPassed(): { data: Driver } | null {
    try {
      const workerData = this.activeRoute.snapshot.params['workerData'];
      return workerData ? JSON.parse(workerData) : null;
    }
    catch {
      return null;
    }
  }

  constructor(
    private workerService: WorkerService,
    private workerHttpService: WorkerHttpService,
    private activeRoute: ActivatedRoute,
    private translateService: TranslateService,
    private userOrganizationsUnitsService: UserOrganizationsUnitsService,
    private usersWorkersService: UsersWorkersService,
    private webAccessService: WebAccessService,
    private permissionService: PermissionService,
    private dialogService: DialogService,
    private notificationsService: NotificationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getGroups();
    this.editedWorkerData = this.workerDataPassed?.data;
    this.onChangeMyCarAccess();
    this.setFormEditedUser();
  }

  override ngOnDestroy(): void {
    this.organizationModalRef?.close();
    this.destroy();
  }

  private get ifAccessToMyCarUndo(): boolean {
    return (this.editedWorkerData?.U && !this.form.value.mycarapp) ? true : false;
  }

  private setFormEditedUser(): void {
    if (this.editedWorkerData) {
      console.log('PRACOWNIK: ', this.editedWorkerData);

      this.initFormData();
      this.isAddNewWorker = false;
    }
  }

  private getGroups(): void {
    this.userOrganizationsUnitsService.GetOwnersForCurrentUser()
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe({
      next: (res) => {
        this.organizations = res;
        this.treeNodeOrganizations = this.mapUnitsToTreeNodes(res);
        this.editedWorkerData && this.initGroupForm();
      }
    });
  }

  private mapUnitsToTreeNodes(units: IOrganizationUnit[], isRoot = true): TreeNode[] {
    return units.map(unit => ({
      label: unit.Name,
      key: unit.Id.toString(),
      data: unit,
      expanded: isRoot,
      children: this.mapUnitsToTreeNodes(unit.SubUnits || [], true),
    }));
  }

  public showGroupTreeModal(): void {
    this.organizationModalRef = this.dialogService.open(TreeSelectionObjectsComponent, {
      header: 'Wybierz grupę',
      width: '500px',
      data: { units: this.organizations },
      closable: true,
    });

    this.organizationModalRef.onClose.pipe(takeUntil(this.destroy$)).subscribe(
      (selected) => {
        if (selected) {
          this.form.patchValue({ organization: selected });
          this.selectedOrganization.set(selected);
        }
      }
    );
  }

  public onChangeMyCarAccess(): void {
    this.form.get('mycarapp')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (value: boolean) => {
        if (value) {
          this.emailControl.setValidators([Validators.required, Validators.email])
        }
        else {
          this.removeValidatorsForm('email');
        }
        this.emailControl.updateValueAndValidity();
        this.form.markAllAsTouched();
      }
    });
  }

  public get driverLicesnseControl() {
    return this.form.get('driverLicense');
  }

  public get emailControl() {
    return this.form.get('email')!;
  }

  public archiveWorker(): void {
    this.workerService.archiveWorkers([this.editedWorkerData as Driver]);
    this.back();
  }

  public back(): void {
    this.onClickBack$.emit();
  }

  public submit(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.isSaving = true;
      const worker = this.prepareDataWorker();
      const driver = this.workerToDriver(worker);

      this.isAddNewWorker ? this.addWorkerToDb(worker, driver) : this.updateWorkerToDb(worker, driver);
      return;
    }
    alert(this.translateService.instant('worker.error-form-add'));
  }

  private addWorkerToDb(worker: WorkerModel, driver: Driver): void {
    this.workerHttpService.add(worker)
    .pipe(
      concatMap((workerID) => (this.hasAccessToCreateAccountUser && this.form.value.mycarapp) ? this.createUserAccountWorker(workerID).pipe(map(() => workerID)) : of(workerID)),
    )
    .subscribe({
      next: (workerID) => {
        this.afterAddWorkerToDb(workerID, driver);
        this.notificationsService.showSuccess(this.translateService.instant('addWorker'));
      },
      error: () => this.onError(),
    });
  }

  private createUserAccountWorker(WorkerId: number): Observable<ResetPasswordResponse> {
    return this.usersWorkersService.CreateUser({ WorkerId, Username: `${ this.form.value.firstName }_${ this.form.value.lastName }` })
    .pipe(
      switchMap(() => this.webAccessService.resetPassword(this.form.value.email)),
      tap(() => this.notificationsService.showSuccess(this.translateService.instant('addUser'))),
    )
  }

  private updateWorkerToDb(worker: WorkerModel, driver: Driver): void {
    this.workerHttpService.update(worker)
    .pipe(
      concatMap(() => {
        if (this.ifAccessToMyCarUndo) {
          return this.usersWorkersService.RemoveUser({ UserId: worker.Id }).pipe(
            map(() => ({ isChangeMyCarAccess: true, value: false })),
            tap(() => this.notificationsService.showSuccess(this.translateService.instant('deleteUser'))),
          )
        }
        if ((!this.editedWorkerData?.U && this.form.value.mycarapp) && this.hasAccessToCreateAccountUser) {
          return this.createUserAccountWorker(worker.Id).pipe(map(() => ({ isChangeMyCarAccess: true, value: true })))
        }
        return of(null);
      })
    )
    .subscribe({
      next: (data) => {
        this.afterUpdateWorker({
          ...driver,
          U: data ? data.value : driver.U,
        });
        this.notificationsService.showSuccess(this.translateService.instant('updateWorker'));
      },
      error: () => this.onError(),
    });
  }

  private onError(): void {
    alert(this.translateService.instant('errorTryAgain'));
    this.isSaving = false;
  }

  private afterUpdateWorker(driver: Driver): void {
    this.workerService.updateWorker(driver);
    this.isSaving = false;
    this.back();
  }

  private afterAddWorkerToDb(workerID: number, driver: Driver): void {
    driver = {
      ...driver,
      Id: workerID,
      U: this.form.value.mycarapp,
    };
    this.workerService.addNewWorkerTolist(driver);
    this.isSaving = false;
    this.back();
  }

  private initFormData(): void {
    this.form.patchValue({
      firstName: this.editedWorkerData?.FN,
      lastName: this.editedWorkerData?.LN,
      phone: this.editedWorkerData?.CN,
      email: this.editedWorkerData?.Em,
      driverLicense: this.editedWorkerData?.L,
      mycarapp: this.editedWorkerData?.U ? true : false,
    });
  }

  private initGroupForm(): void {
    const org = this.treeNodeOrganizations.find(item => item.data?.Id === this.editedWorkerData?.UI);

    org && this.form.patchValue({
      organization: {...org},
    });
  }

  private prepareDataWorker(): WorkerModel {
    const worker = {
      Id: this.editedWorkerData?.Id || 0,
      FirstName: this.form.value.firstName,
      LastName: this.form.value.lastName,
      PhoneNumber: this.form.value.phone,
      CellphoneNumber: this.form.value.phone,
      Email: this.form.value.email,
      FourDriverAccess: false,
      DriverLicenseNumber: this.form.value.driverLicense,
      OrganizationalUnitId: this.form.value.organization.data.Id,
    } as WorkerModel;

    if (this.isAddNewWorker) {
      // worker.OrganizationalUnitId = 1;
      worker.JobPositionId = 1;
      worker.AssignedVehicle = undefined;
    }
    return worker;
  }

  private workerToDriver(worker: WorkerModel): Driver {
    const driver = {} as Driver;
    driver.Id = worker.Id;
    driver.FN = worker.FirstName;
    driver.LN = worker.LastName;
    driver.Pn = worker.PhoneNumber;
    driver.CN = worker.CellphoneNumber;
    driver.Em = worker.Email ?? '';
    driver.L = worker.DriverLicenseNumber ?? '';
    driver.LS = DriverLicesnseStatus.NotCheckedYet;
    return driver;
  }

  private removeValidatorsForm(controlName: string): void {
    this.form.get(controlName)?.clearValidators();
    this.form.get(controlName)?.clearAsyncValidators();
  }
}
